import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  FiEye,
  FiRefreshCw,
  FiMaximize2,
  FiMinimize2,
  FiSmartphone,
  FiTablet,
  FiMonitor,
  FiX,
  FiCode,
  FiDownload,
  FiCopy
} from 'react-icons/fi'
import { VscBrowser, VscDeviceMobile } from 'react-icons/vsc'
import { MdOutlineDevices } from 'react-icons/md'
import { FaFire } from 'react-icons/fa'
import toast from 'react-hot-toast'

const Preview = ({ content }) => {
  const [viewMode, setViewMode] = useState('desktop')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showHtml, setShowHtml] = useState(false)
  const [iframeKey, setIframeKey] = useState(0)

  const viewModes = [
    { id: 'mobile', icon: <FiSmartphone />, width: '375px', label: 'Mobile' },
    { id: 'tablet', icon: <FiTablet />, width: '768px', label: 'Tablet' },
    { id: 'desktop', icon: <FiMonitor />, width: '100%', label: 'Desktop' },
    { id: 'responsive', icon: <MdOutlineDevices />, width: '100%', label: 'Responsive' }
  ]

  const currentMode = viewModes.find(mode => mode.id === viewMode)

  useEffect(() => {
    if (content) {
      setIsRefreshing(true)
      const timer = setTimeout(() => {
        setIsRefreshing(false)
        setIframeKey(prev => prev + 1)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [content])

  const handleRefresh = () => {
    setIsRefreshing(true)
    setIframeKey(prev => prev + 1)
    setTimeout(() => setIsRefreshing(false), 500)
    toast.success('Preview refreshed')
  }

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      const elem = document.querySelector('.preview-container')
      if (elem.requestFullscreen) {
        elem.requestFullscreen()
      }
      setIsFullscreen(true)
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      }
      setIsFullscreen(false)
    }
  }

  const handleDownload = () => {
    const element = document.createElement('a')
    const file = new Blob([content], { type: 'text/html' })
    element.href = URL.createObjectURL(file)
    element.download = 'preview.html'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
    toast.success('HTML file downloaded')
  }

  const handleCopyHtml = () => {
    navigator.clipboard.writeText(content)
    toast.success('HTML copied to clipboard')
  }

  const getIframeContent = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          ${content.includes('<style>') ? '' : `
            body {
              margin: 0;
              padding: 20px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
              background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
            }
            .codefire-preview {
              max-width: 1200px;
              margin: 0 auto;
            }
            .preview-header {
              background: linear-gradient(45deg, #ef4444, #dc2626);
              color: white;
              padding: 20px;
              border-radius: 10px 10px 0 0;
              margin-bottom: 20px;
            }
            .preview-content {
              background: white;
              padding: 30px;
              border-radius: 10px;
              box-shadow: 0 10px 30px rgba(239, 68, 68, 0.1);
            }
          `}
        </style>
      </head>
      <body>
        ${content.includes('<body>') ? content : `
          <div class="codefire-preview">
            <div class="preview-header">
              <h1>🔥 CodeFire Preview</h1>
              <p>Live HTML Preview</p>
            </div>
            <div class="preview-content">
              ${content || '<p>Start writing HTML to see the preview...</p>'}
            </div>
          </div>
        `}
        <script>
          // Prevent default behaviors
          document.addEventListener('contextmenu', e => e.preventDefault());
          console.log('🔥 CodeFire Preview Loaded');
        </script>
      </body>
      </html>
    `
    return htmlContent
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Preview Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800/50">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <VscBrowser className="text-primary-600 dark:text-primary-400" />
            <span className="font-bold text-gray-900 dark:text-white">Preview</span>
          </div>
          <div className="hidden md:flex items-center space-x-1">
            {viewModes.map((mode) => (
              <button
                key={mode.id}
                onClick={() => setViewMode(mode.id)}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg ${
                  viewMode === mode.id
                    ? 'bg-primary-500 text-white'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {mode.icon}
                <span className="text-sm">{mode.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* View Mode Dropdown (Mobile) */}
          <div className="md:hidden">
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm"
            >
              {viewModes.map((mode) => (
                <option key={mode.id} value={mode.id}>
                  {mode.label}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
            title="Refresh Preview"
          >
            {isRefreshing ? (
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <FiRefreshCw />
            )}
          </button>
          <button
            onClick={() => setShowHtml(!showHtml)}
            className={`p-2 rounded-lg ${
              showHtml
                ? 'bg-primary-500 text-white'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title="Show HTML Source"
          >
            <FiCode />
          </button>
          <button
            onClick={handleCopyHtml}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            title="Copy HTML"
          >
            <FiCopy />
          </button>
          <button
            onClick={handleDownload}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            title="Download HTML"
          >
            <FiDownload />
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
          </button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-auto p-4 preview-container">
        {showHtml ? (
          <div className="h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900 dark:text-white">HTML Source</h3>
              <div className="text-sm text-gray-500">
                {content.length} characters
              </div>
            </div>
            <pre className="h-[calc(100%-60px)] bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto font-mono text-sm whitespace-pre-wrap">
              {content || '// No HTML content to display'}
            </pre>
          </div>
        ) : (
          <motion.div
            key={viewMode}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="h-full flex items-center justify-center"
          >
            {isRefreshing ? (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-primary-100 to-red-100 dark:from-gray-800 dark:to-primary-900/20 flex items-center justify-center">
                  <FaFire className="text-3xl text-primary-600 dark:text-primary-400 animate-pulse" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">Refreshing preview...</p>
              </div>
            ) : !content ? (
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-r from-primary-100 to-red-100 dark:from-gray-800 dark:to-primary-900/20 flex items-center justify-center">
                  <FiEye className="text-4xl text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Live Preview
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-sm mx-auto">
                  Write HTML code in the editor to see the live preview here.
                  The preview updates automatically as you type.
                </p>
              </div>
            ) : (
              <div
                className={`bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 ${
                  viewMode === 'responsive' ? 'w-full h-full' : ''
                }`}
                style={{
                  width: currentMode.id !== 'responsive' ? currentMode.width : '100%',
                  maxWidth: currentMode.id === 'mobile' ? '375px' : 
                           currentMode.id === 'tablet' ? '768px' : '100%',
                  height: viewMode === 'responsive' ? '100%' : 'auto',
                  minHeight: '400px'
                }}
              >
                {/* Device Frame */}
                {viewMode === 'mobile' && (
                  <div className="bg-gray-800 p-2 flex items-center justify-center">
                    <div className="w-16 h-1 bg-gray-600 rounded-full"></div>
                  </div>
                )}
                
                {viewMode === 'tablet' && (
                  <div className="bg-gray-800 p-3 flex items-center justify-between">
                    <div className="w-20 h-1 bg-gray-600 rounded-full"></div>
                    <div className="w-8 h-8 rounded-full bg-gray-600"></div>
                  </div>
                )}

                {/* Iframe */}
                <iframe
                  key={iframeKey}
                  srcDoc={getIframeContent()}
                  title="Preview"
                  className="w-full h-full border-0"
                  sandbox="allow-scripts allow-same-origin"
                  style={{
                    minHeight: viewMode === 'desktop' ? '500px' : '600px'
                  }}
                />
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Preview Footer */}
      <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800/50 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              isRefreshing ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'
            }`} />
            <span>{isRefreshing ? 'Refreshing...' : 'Live'}</span>
          </div>
          <span>Mode: {currentMode.label}</span>
        </div>
        <div className="flex items-center space-x-2">
          <span>Auto-refresh: On</span>
          <span>•</span>
          <span>Interactive: Yes</span>
        </div>
      </div>
    </div>
  )
}

export default Preview